import React, { useState, useEffect, useRef, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { RootState } from "../../../store";
import { logout } from "../../../store/slices/authSlice";
import { getUserDisplayName, getUserInitials } from "../../../utils/userUtils";
import {
  Bell, Menu, Search, Check, ChevronDown, LogOut, User, Info,
  CheckCircle, AlertTriangle, XCircle,
} from "lucide-react";
import {
  NotificationService,
  Notification,
} from "../../../services/notifications/notificationService";
import { formatDistanceToNow } from "date-fns";
import { fr, enUS } from "date-fns/locale";
import { useTranslation } from "react-i18next";

interface HeaderProps {
  onMenuClick: () => void;
}

const POLL_INTERVAL_MS = 30_000;

/* ── Icône selon le type de notification ──────────────────────── */
const NotifIcon: React.FC<{ type: Notification["type"] }> = ({ type }) => {
  const cls = "h-4 w-4 flex-shrink-0";
  switch (type) {
    case "success": return <CheckCircle className={`${cls} text-accent-500`} />;
    case "warning": return <AlertTriangle className={`${cls} text-warning-500`} />;
    case "error":   return <XCircle className={`${cls} text-error-500`} />;
    default:        return <Info className={`${cls} text-primary-500`} />;
  }
};

export const Header: React.FC<HeaderProps> = ({ onMenuClick }) => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);

  const [showUserMenu, setShowUserMenu]           = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showLangMenu, setShowLangMenu]           = useState(false);
  const [notifications, setNotifications]         = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount]             = useState(0);
  const [loadingNotifs, setLoadingNotifs]         = useState(false);

  const notifRef = useRef<HTMLDivElement>(null);
  const langRef  = useRef<HTMLDivElement>(null);
  const userRef  = useRef<HTMLDivElement>(null);
  const pollRef  = useRef<ReturnType<typeof setInterval> | null>(null);

  /* ── Fetch notifications (preview) ──────────────────────────── */
  const fetchNotifications = useCallback(async () => {
    try {
      const data = await NotificationService.getNotifications(1);
      setNotifications(data.results.slice(0, 6));
      setUnreadCount(data.results.filter((n) => !n.lue).length);
    } catch (err) {
      console.error("Erreur chargement notifications:", err);
    }
  }, []);

  /* ── Polling unread count (lightweight) ─────────────────────── */
  const pollUnreadCount = useCallback(async () => {
    try {
      const count = await NotificationService.getUnreadCount();
      setUnreadCount(count);
    } catch {
      // silent — network may be unavailable
    }
  }, []);

  useEffect(() => {
    fetchNotifications();

    // Poll unread count every 30 s without fetching full list
    pollRef.current = setInterval(pollUnreadCount, POLL_INTERVAL_MS);

    const handleClickOutside = (e: MouseEvent) => {
      if (notifRef.current && !notifRef.current.contains(e.target as Node))
        setShowNotifications(false);
      if (langRef.current && !langRef.current.contains(e.target as Node))
        setShowLangMenu(false);
      if (userRef.current && !userRef.current.contains(e.target as Node))
        setShowUserMenu(false);
    };
    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [fetchNotifications, pollUnreadCount]);

  /* ── Actions ─────────────────────────────────────────────────── */
  const handleOpenPanel = async () => {
    setShowNotifications(true);
    setShowUserMenu(false);
    setShowLangMenu(false);
    if (notifications.length === 0) {
      setLoadingNotifs(true);
      await fetchNotifications();
      setLoadingNotifs(false);
    }
  };

  const handleTogglePanel = () => {
    if (showNotifications) {
      setShowNotifications(false);
    } else {
      handleOpenPanel();
    }
  };

  const handleMarkAsRead = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    try {
      await NotificationService.markAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, lue: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Erreur markAsRead:", err);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await NotificationService.markAllAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, lue: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Erreur markAllAsRead:", err);
    }
  };

  const handleNotifClick = (notif: Notification) => {
    if (!notif.lue) {
      NotificationService.markAsRead(notif.id).catch(() => {});
      setNotifications((prev) =>
        prev.map((n) => (n.id === notif.id ? { ...n, lue: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    }
    setShowNotifications(false);
    if (notif.lien) navigate(notif.lien);
  };

  const handleSeeAll = () => {
    setShowNotifications(false);
    navigate("/notifications");
  };

  const getLocale = () => (i18n.language.startsWith("fr") ? fr : enUS);

  /* ── Render ──────────────────────────────────────────────────── */
  return (
    <header className="relative bg-white z-10" style={{ borderBottom: "1px solid #E5E7EB" }}>
      <div className="flex items-center justify-between h-16 px-4 sm:px-6">

        {/* Left */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            className="lg:hidden p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
            onClick={onMenuClick}
          >
            <Menu className="h-5 w-5" />
          </button>

          <div className="relative hidden sm:block">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
            <input
              type="text"
              placeholder={t("header.search")}
              className="pl-9 pr-4 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50
                         placeholder-gray-400 text-gray-900 w-60
                         focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 focus:bg-white
                         transition-all duration-150"
            />
          </div>
        </div>

        {/* Right */}
        <div className="flex items-center gap-1">

          {/* Language */}
          <div className="relative" ref={langRef}>
            <button
              onClick={() => { setShowLangMenu(v => !v); setShowNotifications(false); setShowUserMenu(false); }}
              className="flex items-center gap-1 px-2 py-2 rounded-lg text-gray-500
                         hover:text-gray-700 hover:bg-gray-100 transition-colors text-sm font-medium"
            >
              <span className="text-base leading-none">
                {i18n.language.startsWith("fr") ? "🇫🇷" : "🇬🇧"}
              </span>
              <ChevronDown className="h-3 w-3" />
            </button>

            {showLangMenu && (
              <div className="absolute right-0 mt-1.5 w-28 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-50 animate-slide-in">
                {[{ code: "fr", flag: "🇫🇷", label: "Français" }, { code: "en", flag: "🇬🇧", label: "English" }].map(
                  ({ code, flag, label }) => (
                    <button
                      key={code}
                      onClick={() => { i18n.changeLanguage(code); setShowLangMenu(false); }}
                      className={`flex items-center gap-2 w-full px-3 py-2 text-sm text-left transition-colors
                        ${i18n.language.startsWith(code) ? "bg-primary-50 text-primary-700 font-medium" : "text-gray-700 hover:bg-gray-50"}`}
                    >
                      <span>{flag}</span><span>{label}</span>
                    </button>
                  )
                )}
              </div>
            )}
          </div>

          {/* Notifications */}
          <div className="relative" ref={notifRef}>
            <button
              onClick={handleTogglePanel}
              className="relative p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-label="Notifications"
            >
              <Bell className="h-5 w-5" />
              {unreadCount > 0 && (
                <span
                  className="absolute top-1 right-1 min-w-[18px] h-[18px] flex items-center justify-center
                             rounded-full text-[10px] font-bold text-white px-1"
                  style={{ backgroundColor: "#10B981" }}
                >
                  {unreadCount > 99 ? "99+" : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-1.5 w-96 bg-white rounded-xl shadow-xl
                              border border-gray-100 overflow-hidden z-50 animate-slide-in">
                {/* Header du panel */}
                <div className="flex items-center justify-between px-4 py-3"
                     style={{ borderBottom: "1px solid #F3F4F6" }}>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-gray-900">
                      {t("header.notifications")}
                    </p>
                    {unreadCount > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-accent-100 text-accent-700">
                        {unreadCount} non lue{unreadCount > 1 ? "s" : ""}
                      </span>
                    )}
                  </div>
                  {unreadCount > 0 && (
                    <button
                      onClick={handleMarkAllAsRead}
                      className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors"
                    >
                      <Check className="h-3 w-3" />
                      Tout marquer lu
                    </button>
                  )}
                </div>

                {/* Liste */}
                <div className="max-h-80 overflow-y-auto">
                  {loadingNotifs ? (
                    <div className="flex items-center justify-center py-10">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600" />
                    </div>
                  ) : notifications.length === 0 ? (
                    <div className="flex flex-col items-center py-10 text-gray-400">
                      <Bell className="h-8 w-8 mb-2 opacity-30" />
                      <p className="text-sm font-medium">Aucune notification</p>
                      <p className="text-xs mt-1 text-gray-300">Vous êtes à jour !</p>
                    </div>
                  ) : (
                    <div className="divide-y divide-gray-50">
                      {notifications.map((notif) => (
                        <button
                          key={notif.id}
                          onClick={() => handleNotifClick(notif)}
                          className={`w-full text-left flex items-start gap-3 px-4 py-3 transition-colors group
                            ${!notif.lue
                              ? "bg-primary-50/50 hover:bg-primary-50"
                              : "hover:bg-gray-50"
                            }`}
                        >
                          {/* Icône type */}
                          <div className={`mt-0.5 p-1.5 rounded-lg flex-shrink-0
                            ${notif.type === "success" ? "bg-accent-50"
                              : notif.type === "warning" ? "bg-warning-50"
                              : notif.type === "error" ? "bg-error-50"
                              : "bg-primary-50"}`}
                          >
                            <NotifIcon type={notif.type} />
                          </div>

                          {/* Contenu */}
                          <div className="flex-1 min-w-0">
                            <p className={`text-sm font-semibold truncate ${!notif.lue ? "text-gray-900" : "text-gray-600"}`}>
                              {notif.titre}
                            </p>
                            <p className="text-xs text-gray-500 mt-0.5 line-clamp-2 leading-relaxed">
                              {notif.message}
                            </p>
                            <p className="text-[10px] text-gray-400 mt-1">
                              {formatDistanceToNow(new Date(notif.timestamp), {
                                addSuffix: true,
                                locale: getLocale(),
                              })}
                            </p>
                          </div>

                          {/* Indicateur non lu + bouton marquer lu */}
                          <div className="flex-shrink-0 flex flex-col items-center gap-2 pt-1">
                            {!notif.lue && (
                              <>
                                <div className="h-2 w-2 rounded-full bg-accent-500" />
                                <button
                                  onClick={(e) => handleMarkAsRead(notif.id, e)}
                                  className="opacity-0 group-hover:opacity-100 p-1 rounded text-gray-400
                                             hover:text-primary-600 transition-all"
                                  title="Marquer comme lu"
                                >
                                  <Check className="h-3 w-3" />
                                </button>
                              </>
                            )}
                          </div>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                <div className="px-4 py-2.5 text-center" style={{ borderTop: "1px solid #F3F4F6" }}>
                  <button
                    onClick={handleSeeAll}
                    className="text-xs font-semibold text-primary-600 hover:text-primary-700 transition-colors"
                  >
                    Voir toutes les notifications →
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Divider */}
          <div className="h-6 w-px bg-gray-200 mx-1" />

          {/* User menu */}
          <div className="relative" ref={userRef}>
            <button
              onClick={() => { setShowUserMenu(v => !v); setShowNotifications(false); setShowLangMenu(false); }}
              className="flex items-center gap-2.5 pl-1 pr-2 py-1.5 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none"
            >
              <div
                className="h-8 w-8 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0"
                style={{ backgroundColor: "#1E3A8A" }}
              >
                {getUserInitials(user)}
              </div>
              <div className="hidden md:flex flex-col items-start">
                <span className="text-sm font-semibold text-gray-800 leading-tight truncate max-w-[120px]">
                  {getUserDisplayName(user)}
                </span>
                <span className="text-xs text-gray-400 capitalize leading-tight">
                  {user?.role ? t(`roles.${user.role}`) : ""}
                </span>
              </div>
              <ChevronDown className="hidden md:block h-3.5 w-3.5 text-gray-400" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-1.5 w-52 bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden z-50 animate-slide-in">
                <div className="px-4 py-3" style={{ borderBottom: "1px solid #F3F4F6" }}>
                  <p className="text-sm font-semibold text-gray-900 truncate">{getUserDisplayName(user)}</p>
                  <p className="text-xs text-gray-500 truncate mt-0.5">{user?.email}</p>
                </div>
                <div className="py-1">
                  <Link
                    to="/profile"
                    onClick={() => setShowUserMenu(false)}
                    className="flex items-center gap-2.5 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    <User className="h-4 w-4 text-gray-400" />
                    Mon profil
                  </Link>
                  <button
                    onClick={() => dispatch(logout())}
                    className="flex items-center gap-2.5 w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
                  >
                    <LogOut className="h-4 w-4" />
                    Déconnexion
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};
